// ─────────────────────────────────────────────────────────────────
// Glass-i — Glass Type Routing page (Stage WO-2A).
//
// Business rules WO-2:
//   1. Departments are always traversed in the Production Departments
//      execution_order — so the stage COLUMNS here are fixed/ordered and
//      NOT re-orderable; you only tick which stages a type visits.
//   2. Each glass Type gets its own route (the ticked stages).
//   3. Stages a type skips won't count that line/pcs on the board / reports.
//
// A type with zero ticked stages is "unconfigured" — releasing a Work Order
// for it is blocked (enforced server-side). Save persists the whole matrix.
// ─────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, Input, Table, Tooltip, Space, Alert, Spin, Tag, message } from 'antd';
import {
  PartitionOutlined, SearchOutlined, ReloadOutlined, SaveOutlined,
  LockFilled, TagsOutlined
} from '@ant-design/icons';
import { glassTypeRoutingApi } from './glassTypeRoutingApi';
import {
  buildSelections, toggleStage, hasStage, setAllStages, isDirty,
  changedSelections, unconfiguredTypes, matchesSearch
} from './glassTypeRoutingHelpers';
import './GlassTypeRoutingPage.css';

export default function GlassTypeRoutingPage() {
  const [stages, setStages] = useState([]);
  const [types, setTypes] = useState([]);
  const [selections, setSelections] = useState({});
  const [original, setOriginal] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const m = await glassTypeRoutingApi.matrix();
      const stg = (m && m.stages) || [];
      const tps = ((m && m.glassTypes) || []).map((t) => t.glassType || t);
      const sel = buildSelections(m);
      setStages(stg);
      setTypes(tps);
      setSelections(sel);
      setOriginal(sel);
    } catch (e) {
      message.error((e && e.response && e.response.data && e.response.data.error && e.response.data.error.message) || 'Failed to load routing.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const allGroups = useMemo(() => stages.map((s) => s.departmentCode), [stages]);
  const dirty = useMemo(() => isDirty(selections, original), [selections, original]);
  const unconfigured = useMemo(() => unconfiguredTypes(selections), [selections]);
  const dirtyCount = useMemo(
    () => Object.keys(changedSelections(selections, original)).length,
    [selections, original]
  );

  const filteredTypes = useMemo(
    () => types.filter((t) => matchesSearch(t, search)),
    [types, search]
  );

  const onToggle = useCallback((glassType, group) => {
    setSelections((s) => toggleStage(s, glassType, group));
  }, []);
  const onRowAll = useCallback((glassType, on) => {
    setSelections((s) => setAllStages(s, glassType, allGroups, on));
  }, [allGroups]);

  const save = useCallback(async () => {
    const changed = changedSelections(selections, original);
    if (!Object.keys(changed).length) return;
    setSaving(true);
    try {
      const res = await glassTypeRoutingApi.bulkSave(changed);
      message.success(`Saved ${res.types} type${res.types === 1 ? '' : 's'} · +${res.added} / −${res.removed} stages.`);
      setOriginal(selections);
    } catch (e) {
      message.error((e && e.response && e.response.data && e.response.data.error && e.response.data.error.message) || 'Failed to save routing.');
    } finally { setSaving(false); }
  }, [selections, original]);

  const columns = useMemo(() => {
    const cols = [{
      title: 'Glass Type',
      dataIndex: 'glassType',
      key: 'glassType',
      fixed: 'left',
      width: 150,
      render: (t) => (
        <span className="gtr-type">
          <code className="gtr-type__code">{t}</code>
          {!(selections[t] && selections[t].length) ? (
            <Tag color="red" className="gtr-type__warn">no route</Tag>
          ) : null}
        </span>
      )
    }];
    for (const s of stages) {
      cols.push({
        title: (
          <Tooltip title={s.departmentName || s.label}>
            <span className="gtr-col">
              <span className="gtr-col__name">
                {s.letter || s.label}
                {s.isLockMilestone ? <LockFilled className="gtr-col__lock" /> : null}
                {s.isRelabelStation ? <TagsOutlined className="gtr-col__relabel" /> : null}
              </span>
            </span>
          </Tooltip>
        ),
        key: s.departmentCode,
        align: 'center',
        width: 92,
        render: (_v, row) => (
          <Checkbox
            checked={hasStage(selections, row.glassType, s.departmentCode)}
            onChange={() => onToggle(row.glassType, s.departmentCode)}
          />
        )
      });
    }
    cols.push({
      title: 'All',
      key: '_all',
      align: 'center',
      width: 84,
      fixed: 'right',
      render: (_v, row) => (
        <Space size={2}>
          <Button size="small" type="link" className="gtr-rowbtn" onClick={() => onRowAll(row.glassType, true)}>All</Button>
          <span className="gtr-rowbtn__sep">/</span>
          <Button size="small" type="link" className="gtr-rowbtn" onClick={() => onRowAll(row.glassType, false)}>None</Button>
        </Space>
      )
    });
    return cols;
  }, [stages, selections, onToggle, onRowAll]);

  const dataSource = useMemo(
    () => filteredTypes.map((t) => ({ key: t, glassType: t })),
    [filteredTypes]
  );

  return (
    <div className="gtr-shell">
      <div className="gtr-headstrip">
        <div className="gtr-headstrip__lead">
          <span className="gtr-headstrip__icon"><PartitionOutlined /></span>
          <div className="gtr-headstrip__text">
            <span className="gtr-headstrip__title">Glass Type Routing</span>
            <span className="gtr-headstrip__subtitle">
              Tick the production stages each glass type passes through. The stage order is fixed by the Production Departments master.
            </span>
          </div>
        </div>
        <Space size={6}>
          <span className="gtr-chip"><strong>{types.length}</strong> types</span>
          {unconfigured.length > 0 && (
            <span className="gtr-chip gtr-chip--warn"><strong>{unconfigured.length}</strong> unconfigured</span>
          )}
          {dirtyCount > 0 && (
            <span className="gtr-chip gtr-chip--dirty"><strong>{dirtyCount}</strong> unsaved</span>
          )}
        </Space>
      </div>

      {unconfigured.length > 0 && (
        <Alert
          type="warning" showIcon className="gtr-alert"
          message={
            <span>
              {unconfigured.length} glass type{unconfigured.length === 1 ? '' : 's'} have no route configured —
              releasing a Work Order for {unconfigured.length === 1 ? 'it' : 'them'} will be blocked until a route is set.
            </span>
          }
        />
      )}

      <div className="gtr-toolbar">
        <Input
          allowClear size="small"
          prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
          placeholder="Search glass type…"
          value={search} onChange={(e) => setSearch(e.target.value)}
          style={{ width: 240 }}
        />
        <div className="gtr-toolbar__spacer" />
        <Tooltip title="Reload"><Button size="small" icon={<ReloadOutlined />} onClick={load} loading={loading} /></Tooltip>
        <Button
          size="small" type="primary" icon={<SaveOutlined />}
          onClick={save} loading={saving} disabled={saving || !dirty}
        >
          Save Changes
        </Button>
      </div>

      <Spin spinning={loading}>
        <Table
          className="gtr-table"
          size="small"
          columns={columns}
          dataSource={dataSource}
          pagination={false}
          scroll={{ x: 'max-content', y: 560 }}
          bordered
          sticky
        />
      </Spin>
    </div>
  );
}
